import { useState, useEffect } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	Button,
	Text,
	Link,
	Spinner,
	CloseButton,
	Image,
	Box,
	Flex
} from "@chakra-ui/react";
import { FaTwitter, FaTelegramPlane, FaDiscord } from 'react-icons/fa'; // Add FaDiscord import
import thankGif from '../assets/images/gif/thanks.gif';
import { CheckCircleIcon } from '@chakra-ui/icons';

// URLs
const TWITTER_FOUNDER_URL = 'https://x.com/intent/follow?screen_name=vadimsemenko';
const TWITTER_URL = 'https://x.com/intent/follow?screen_name=scrollstat';
const TELEGRAM_URL = 'https://t.me/scrollstat';
const DISCORD_URL = 'https://discord.gg/vDu6ay7v'; // Add your Discord URL here
const TWITTER_RETWEET_URL = 'https://x.com/intent/retweet?tweet_id=1824508933656236537';

const LOCAL_STORAGE_KEY_TWITTER = 'twitterSubscribed';
const LOCAL_STORAGE_KEY_TELEGRAM = 'telegramSubscribed';
const LOCAL_STORAGE_KEY_DISCORD = 'discordSubscribed'; // Local storage key for Discord
const LOCAL_STORAGE_KEY_FOUNDER_TWITTER = 'twitterFounderSubscribed';
const LOCAL_STORAGE_KEY_RETWEET = 'retweetCompleted';

const SubscriptionModal = ({ isOpen, onClose, onTwitterSubscribe, onTelegramSubscribe, onTwitterFounderSubscribe, onTwitterPostRetweet, onDiscordInvite }) => {
	const [isProcessingTwitter, setIsProcessingTwitter] = useState(false);
	const [isProcessingTelegram, setIsProcessingTelegram] = useState(false);
	const [isProcessingDiscord, setIsProcessingDiscord] = useState(false); // State for Discord
	const [isProcessingSeoTwitter, setIsProcessingSeoTwitter] = useState(false);
	const [isProcessingRetweet, setIsProcessingRetweet] = useState(false);
	const [completedTasks, setCompletedTasks] = useState({ twitter: false, telegram: false, discord: false, seoTwitter: false, retweet: false });
	const [allTasksCompleted, setAllTasksCompleted] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const openModal = () => setIsModalOpen(true);
	const closeModal = () => setIsModalOpen(false);

	useEffect(() => {
		if (isOpen) {
			const twitterSubscribed = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_TWITTER)) || false;
			const telegramSubscribed = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_TELEGRAM)) || false;
			const discordSubscribed = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_DISCORD)) || false; // Get Discord subscription status
			const seoTwitterSubscribed = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_FOUNDER_TWITTER)) || false;
			const retweetCompleted = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_RETWEET)) || false;
			setCompletedTasks({ twitter: twitterSubscribed, telegram: telegramSubscribed, discord: discordSubscribed, seoTwitter: seoTwitterSubscribed, retweet: retweetCompleted });
			setAllTasksCompleted(twitterSubscribed && telegramSubscribed && discordSubscribed && seoTwitterSubscribed && retweetCompleted);
		}
	}, [isOpen]);

	const handleSubscribeTwitter = () => {
		if (completedTasks.twitter) return;
		setIsProcessingTwitter(true);
		onTwitterSubscribe();
		setTimeout(() => {
			setIsProcessingTwitter(false);
			localStorage.setItem(LOCAL_STORAGE_KEY_TWITTER, JSON.stringify(true));
			setCompletedTasks(prevState => {
				const updatedTasks = { ...prevState, twitter: true };
				if (updatedTasks.twitter && updatedTasks.telegram && updatedTasks.discord && updatedTasks.seoTwitter && updatedTasks.retweet) {
					setAllTasksCompleted(true);
				}
				return updatedTasks;
			});
		}, 20000);
	};

	const handleSubscribeTelegram = () => {
		if (completedTasks.telegram) return;
		setIsProcessingTelegram(true);
		onDiscordInvite();
		setTimeout(() => {
			setIsProcessingTelegram(false);
			localStorage.setItem(LOCAL_STORAGE_KEY_TELEGRAM, JSON.stringify(true));
			setCompletedTasks(prevState => {
				const updatedTasks = { ...prevState, telegram: true };
				if (updatedTasks.twitter && updatedTasks.telegram && updatedTasks.discord && updatedTasks.seoTwitter && updatedTasks.retweet) {
					setAllTasksCompleted(true);
				}
				return updatedTasks;
			});
		}, 20000);
	};

	const handleSubscribeDiscord = () => {
		if (completedTasks.discord) return;
		setIsProcessingDiscord(true);
		// Assuming you want to redirect the user to Discord directly
		window.open(DISCORD_URL, '_blank');
		setTimeout(() => {
			setIsProcessingDiscord(false);
			localStorage.setItem(LOCAL_STORAGE_KEY_DISCORD, JSON.stringify(true));
			setCompletedTasks(prevState => {
				const updatedTasks = { ...prevState, discord: true };
				if (updatedTasks.twitter && updatedTasks.telegram && updatedTasks.discord && updatedTasks.seoTwitter && updatedTasks.retweet) {
					setAllTasksCompleted(true);
				}
				return updatedTasks;
			});
		}, 20000);
	};

	const handleSubscribeSeoTwitter = () => {
		if (completedTasks.seoTwitter) return;
		setIsProcessingSeoTwitter(true);
		onTwitterFounderSubscribe();
		setTimeout(() => {
			setIsProcessingSeoTwitter(false);
			localStorage.setItem(LOCAL_STORAGE_KEY_FOUNDER_TWITTER, JSON.stringify(true));
			setCompletedTasks(prevState => {
				const updatedTasks = { ...prevState, seoTwitter: true };
				if (updatedTasks.twitter && updatedTasks.telegram && updatedTasks.discord && updatedTasks.seoTwitter && updatedTasks.retweet) {
					setAllTasksCompleted(true);
				}
				return updatedTasks;
			});
		}, 20000);
	};

	const handleSubscribeRetweet = () => {
		if (completedTasks.retweet) return;
		setIsProcessingRetweet(true);
		onTwitterPostRetweet();
		setTimeout(() => {
			setIsProcessingRetweet(false);
			localStorage.setItem(LOCAL_STORAGE_KEY_RETWEET, JSON.stringify(true));
			setCompletedTasks(prevState => {
				const updatedTasks = { ...prevState, retweet: true };
				if (updatedTasks.twitter && updatedTasks.telegram && updatedTasks.discord && updatedTasks.seoTwitter && updatedTasks.retweet) {
					setAllTasksCompleted(true);
				}
				return updatedTasks;
			});
		}, 20000);
	};

	return (
		<>
			<Modal isOpen={isOpen} onClose={onClose} size={{ base: "sm", md: "lg" }}>
				<ModalOverlay />
				<ModalContent p={4} mx={{ base: 2, md: 4 }} my={{ base: 4, md: 8 }}>
					<ModalHeader fontSize={{ base: "md", md: "lg" }} color="black" pb={1}>
						{!allTasksCompleted ? 'Continue Your Journey – Follow Us' : ''}
						{allTasksCompleted && (
							<CloseButton position="absolute" right="8px" top="8px" onClick={onClose} />
						)}
					</ModalHeader>
					<ModalBody pb={4}>
						{!allTasksCompleted ? (
							<>
								<Text mb={4} fontSize={{ base: "sm", md: "md" }}>
									To keep enjoying our free service, please complete the following steps:
								</Text>

								<Flex direction="column" gap={4} align="center" pt={4} pb={4}>
									{/* Twitter Subscription */}
									<Flex align="center" gap={2} width="full" justifyContent={"space-between"}>
										<Flex gap={4}>
											<FaTwitter size="24px" color="#1DA1F2" />
											<Box flex="1" textAlign="center">
												<Text mb={1} fontSize={{ base: "sm", md: "md" }}>Join us on Twitter for the latest updates</Text>
											</Box>
										</Flex>
										<Box textAlign="right">
											{!completedTasks.twitter ?
												<Link href={TWITTER_URL} isExternal>
													<Button
														colorScheme="blue"
														onClick={handleSubscribeTwitter}
														width="auto"
														minWidth="120px"
														size="sm"
														isDisabled={isProcessingTwitter || completedTasks.twitter}
													>
														{isProcessingTwitter ? <Spinner size="sm" /> : 'Join'}
													</Button>
												</Link>
												:
												<Flex justifyContent={"center"} alignContent={"center"} alignItems={"center"} minWidth="120px">
													<CheckCircleIcon color={"green"}></CheckCircleIcon>
												</Flex>
											}
										</Box>
									</Flex>

									{/* Telegram Subscription */}
									<Flex align="center" gap={2} width="full" justifyContent={"space-between"}>
										<Flex gap={4}>
											<FaTelegramPlane size="24px" color="#0088cc" />
											<Box flex="1" textAlign="center">
												<Text mb={1} fontSize={{ base: "sm", md: "md" }}>Join our Telegram community</Text>
											</Box>
										</Flex>
										<Box textAlign="right">
											{!completedTasks.telegram ?
												<Link href={TELEGRAM_URL} isExternal>
													<Button
														colorScheme="blue"
														onClick={handleSubscribeTelegram}
														width="auto"
														minWidth="120px"
														size="sm"
														isDisabled={isProcessingTelegram || completedTasks.telegram}
													>
														{isProcessingTelegram ? <Spinner size="sm" /> : 'Join'}
													</Button>
												</Link>
												:
												<Flex justifyContent={"center"} alignContent={"center"} alignItems={"center"} minWidth="120px">
													<CheckCircleIcon color={"green"}></CheckCircleIcon>
												</Flex>
											}
										</Box>
									</Flex>

									{/* Discord Subscription */}
									<Flex align="center" gap={2} width="full" justifyContent={"space-between"}>
										<Flex gap={4}>
											<FaDiscord size="24px" color="#7289DA" /> {/* Discord Icon */}
											<Box flex="1" textAlign="center">
												<Text mb={1} fontSize={{ base: "sm", md: "md" }}>Join our Discord server </Text>
											</Box>
										</Flex>
										<Box textAlign="right">
											{!completedTasks.discord ?
												<Button
													colorScheme="blue"
													onClick={handleSubscribeDiscord}
													width="auto"
													minWidth="120px"
													size="sm"
													isDisabled={isProcessingDiscord || completedTasks.discord}
												>
													{isProcessingDiscord ? <Spinner size="sm" /> : 'Join'}
												</Button>
												:
												<Flex justifyContent={"center"} alignContent={"center"} alignItems={"center"} minWidth="120px">
													<CheckCircleIcon color={"green"}></CheckCircleIcon>
												</Flex>
											}
										</Box>
									</Flex>

									{/* Founder Twitter Subscription */}
									<Flex align="center" gap={2} width="full" justifyContent={"space-between"}>
										<Flex gap={4}>
											<FaTwitter size="24px" color="#1DA1F2" />
											<Box flex="1" textAlign="center">
												<Text mb={1} fontSize={{ base: "sm", md: "md" }}>Follow our Founder on Twitter</Text>
											</Box>
										</Flex>
										<Box textAlign="right">
											{!completedTasks.seoTwitter ?
												<Link href={TWITTER_FOUNDER_URL} isExternal>
													<Button
														colorScheme="blue"
														onClick={handleSubscribeSeoTwitter}
														width="auto"
														minWidth="120px"
														size="sm"
														isDisabled={isProcessingSeoTwitter || completedTasks.seoTwitter}
													>
														{isProcessingSeoTwitter ? <Spinner size="sm" /> : 'Join'}
													</Button>
												</Link>
												:
												<Flex justifyContent={"center"} alignContent={"center"} alignItems={"center"} minWidth="120px">
													<CheckCircleIcon color={"green"}></CheckCircleIcon>
												</Flex>
											}
										</Box>
									</Flex>

									{/* Retweet Subscription */}
									<Flex align="center" gap={2} width="full" justifyContent={"space-between"}>
										<Flex gap={4}>
											<FaTwitter size="24px" color="#1DA1F2" />
											<Box flex="1" textAlign="center">
												<Text mb={1} fontSize={{ base: "sm", md: "md" }}>Retweet our post</Text>
											</Box>
										</Flex>
										<Box textAlign="right">
											{!completedTasks.retweet ?
												<Link href={TWITTER_RETWEET_URL} isExternal>
													<Button
														colorScheme="blue"
														onClick={handleSubscribeRetweet}
														width="auto"
														minWidth="120px"
														size="sm"
														isDisabled={isProcessingRetweet || completedTasks.retweet}
													>
														{isProcessingRetweet ? <Spinner size="sm" /> : 'Retweet'}
													</Button>
												</Link>
												:
												<Flex justifyContent={"center"} alignContent={"center"} alignItems={"center"} minWidth="120px">
													<CheckCircleIcon color={"green"}></CheckCircleIcon>
												</Flex>
											}
										</Box>
									</Flex>
								</Flex>
							</>
						) : (
							<>
								<Image src={thankGif} alt="Thank You" />
								<Text fontSize={{ base: "lg", md: "xl" }} textAlign="center">
									Thank you for your support! You can now enjoy our service.
								</Text>
							</>
						)}
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	);
};

export default SubscriptionModal;
